import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { users as usersApi, auth } from '../api';
import { useAuth } from '../App';
import { Plus, X, UserCheck, UserX } from 'lucide-react';

// ── USER MANAGEMENT ────────────────────────────────────────────────────────────
function UserModal({ onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ username: '', password: '', office_access: 'ahmedabad' });
  const [loading, setLoading] = useState(false);

  const save = async () => {
    if (!form.username || !form.password) return toast.error('All fields required');
    setLoading(true);
    try {
      await usersApi.create(form);
      toast.success('User created');
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 className="modal-title" style={{ margin: 0 }}>{t('createUser')}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label className="label">{t('username')}</label>
            <input className="input" value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))} />
          </div>
          <div>
            <label className="label">{t('password')}</label>
            <input className="input" type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} />
          </div>
          <div>
            <label className="label">{t('officeAccess')}</label>
            <select className="input" value={form.office_access} onChange={e => setForm(p => ({ ...p, office_access: e.target.value }))}>
              <option value="ahmedabad">{t('ahmedabad')}</option>
              <option value="palanpur">{t('palanpur')}</option>
              <option value="both">{t('both')}</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={onClose}>{t('cancel')}</button>
            <button className="btn btn-primary" onClick={save} disabled={loading}>{loading ? 'Creating...' : t('createUser')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function UserManagement() {
  const { t } = useTranslation();
  const [userList, setUserList] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const load = async () => {
    try { const r = await usersApi.list(); setUserList(r.data); } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (u) => {
    await usersApi.update(u.id, { is_active: !u.is_active });
    toast.success(`User ${u.is_active ? 'deactivated' : 'activated'}`);
    load();
  };

  const updateOffice = async (u, office_access) => {
    await usersApi.update(u.id, { office_access });
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary)' }}>{t('userManagement')}</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={15} /> {t('createUser')}
        </button>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="table">
          <thead>
            <tr>
              <th>{t('username')}</th>
              <th>{t('officeAccess')}</th>
              <th>{t('status')}</th>
              <th>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {userList.length === 0 ? (
              <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--gray)', padding: 32 }}>No staff users yet</td></tr>
            ) : userList.map(u => (
              <tr key={u.id}>
                <td style={{ fontWeight: 600 }}>{u.username}</td>
                <td>
                  <select className="input" style={{ width: 'auto', padding: '4px 8px', fontSize: 12 }}
                    value={u.office_access} onChange={e => updateOffice(u, e.target.value)}>
                    <option value="ahmedabad">{t('ahmedabad')}</option>
                    <option value="palanpur">{t('palanpur')}</option>
                    <option value="both">{t('both')}</option>
                  </select>
                </td>
                <td><span className={`badge ${u.is_active ? 'badge-success' : 'badge-danger'}`}>{u.is_active ? t('active') : t('inactive')}</span></td>
                <td>
                  <button className={`btn btn-sm ${u.is_active ? 'btn-danger' : 'btn-success'}`} onClick={() => toggleActive(u)}>
                    {u.is_active ? <><UserX size={13} /> {t('deactivate')}</> : <><UserCheck size={13} /> Activate</>}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showModal && <UserModal onClose={() => setShowModal(false)} onSaved={load} />}
    </div>
  );
}

export default UserManagement;

// ── SETTINGS ────────────────────────────────────────────────────────────────────
export function Settings() {
  const { t, i18n } = useTranslation();
  const { user, switchLanguage } = useAuth();
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [loading, setLoading] = useState(false);

  const changePass = async () => {
    if (!form.current_password || !form.new_password) return toast.error('All fields required');
    if (form.new_password !== form.confirm) return toast.error('New passwords do not match');
    setLoading(true);
    try {
      await auth.changePassword(form.current_password, form.new_password);
      toast.success('Password changed successfully');
      setForm({ current_password: '', new_password: '', confirm: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    setLoading(false);
  };

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary)', marginBottom: 24 }}>{t('settings')}</h1>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, maxWidth: 800 }}>
        <div className="card">
          <h3 style={{ fontWeight: 700, color: 'var(--primary)', marginBottom: 16 }}>{t('changePassword')}</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label className="label">{t('currentPassword')}</label>
              <input className="input" type="password" value={form.current_password}
                onChange={e => setForm(p => ({ ...p, current_password: e.target.value }))} />
            </div>
            <div>
              <label className="label">{t('newPassword')}</label>
              <input className="input" type="password" value={form.new_password}
                onChange={e => setForm(p => ({ ...p, new_password: e.target.value }))} />
            </div>
            <div>
              <label className="label">Confirm New Password</label>
              <input className="input" type="password" value={form.confirm}
                onChange={e => setForm(p => ({ ...p, confirm: e.target.value }))} />
            </div>
            <button className="btn btn-primary" onClick={changePass} disabled={loading} style={{ marginTop: 4 }}>
              {loading ? 'Saving...' : t('changePassword')}
            </button>
          </div>
        </div>

        <div className="card">
          <h3 style={{ fontWeight: 700, color: 'var(--primary)', marginBottom: 16 }}>{t('language')}</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[{ code: 'en', label: 'English' }, { code: 'gu', label: 'ગુજરાતી (Gujarati)' }].map(lang => (
              <label key={lang.code} style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', padding: 12, borderRadius: 8, border: `2px solid ${i18n.language === lang.code ? 'var(--accent)' : 'var(--border)'}`, background: i18n.language === lang.code ? 'var(--accent-light)' : 'white' }}>
                <input type="radio" checked={i18n.language === lang.code} onChange={() => switchLanguage(lang.code)} />
                <span style={{ fontWeight: i18n.language === lang.code ? 600 : 400 }}>{lang.label}</span>
              </label>
            ))}
          </div>

          <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
            <div className="section-label">Account Info</div>
            <div style={{ fontSize: 14, color: 'var(--gray)', marginTop: 8 }}>
              <div>Username: <strong style={{ color: 'var(--primary)' }}>{user?.username}</strong></div>
              <div style={{ marginTop: 4 }}>Role: <strong style={{ color: 'var(--primary)', textTransform: 'capitalize' }}>{user?.role}</strong></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
